/**
 * One conversation: header (title, participants, pop-out), messages, composer. Marks the
 * newest message read when shown (the marker moves forward only, server side).
 */
import type { ThreadDto } from '@oex/contracts';
import { Alert, Button, Skeleton } from '@oex/ui';
import { Bell, BellOff, ExternalLink, X } from 'lucide-react';
import { useEffect } from 'react';
import { useT } from '../../../app/i18n';
import { useSession } from '../../../app/session';
import { useUiSettings } from '../../../app/ui-settings';
import { useMarkRead, useMessages, useThread } from '../api';
import { openPopout } from '../popout';
import { Composer } from './composer';
import { threadTitle } from './labels';
import { MessageList } from './message-list';

export function ThreadHeader({
  thread,
  onClose,
  popout = true,
}: {
  thread: ThreadDto;
  onClose?: () => void;
  popout?: boolean;
}) {
  const t = useT();
  const { me, activeOrgId } = useSession();
  const { settings, save } = useUiSettings();
  const muted = (settings.mutedThreadIds ?? []).includes(thread.id);
  const toggleMute = () => {
    const cur = settings.mutedThreadIds ?? [];
    void save({ mutedThreadIds: muted ? cur.filter((x) => x !== thread.id) : [...cur, thread.id] });
  };
  const title = threadTitle(t, thread, me?.user.id ?? null, activeOrgId);
  return (
    <div className="flex items-start justify-between gap-2 border-b border-border pb-2">
      <div className="min-w-0">
        <h2 className="truncate font-display text-base font-semibold">
          {title}
          {muted ? <BellOff className="ml-1 inline size-3.5 text-muted-foreground" aria-label={t('messaging.thread.muted')} /> : null}
        </h2>
        <p className="truncate text-xs text-muted-foreground">
          {t('messaging.thread.participants', { count: thread.participantCount })}
          {thread.emailParticipants.length
            ? ` · ${t('messaging.thread.byEmail', { count: thread.emailParticipants.length })}`
            : ''}
        </p>
      </div>
      <div className="flex gap-1">
        <Button
          size="sm"
          variant="secondary"
          aria-label={muted ? t('messaging.thread.unmute') : t('messaging.thread.mute')}
          aria-pressed={muted}
          onClick={toggleMute}
        >
          {muted ? <BellOff className="size-3.5" aria-hidden /> : <Bell className="size-3.5" aria-hidden />}
        </Button>
        {popout ? (
          <Button
            size="sm"
            variant="secondary"
            aria-label={t('messaging.thread.popout')}
            onClick={() => openPopout(thread.id, title)}
          >
            <ExternalLink className="size-3.5" aria-hidden />
          </Button>
        ) : null}
        {onClose ? (
          <Button size="sm" variant="secondary" aria-label={t('app.close')} onClick={onClose}>
            <X className="size-3.5" aria-hidden />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function ThreadView({
  threadId,
  onClose,
  popout = true,
}: {
  threadId: string;
  onClose?: () => void;
  popout?: boolean;
}) {
  const t = useT();
  const { me } = useSession();
  const thread = useThread(threadId);
  const messages = useMessages(threadId);
  const markRead = useMarkRead();
  const items = messages.data?.items ?? [];
  const last = items[items.length - 1];
  const unread = thread.data?.unreadCount ?? 0;
  useEffect(() => {
    if (last && unread > 0 && !markRead.isPending)
      markRead.mutate({ id: threadId, data: { lastReadMessageId: last.id } });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mark once per newest message
  }, [threadId, last?.id, unread]);
  if (thread.isLoading) return <Skeleton className="h-40" />;
  if (!thread.data) return <Alert tone="warning">{t('messaging.thread.missing')}</Alert>;
  return (
    <div className="flex h-full min-h-0 flex-col gap-3" data-thread={threadId}>
      <ThreadHeader thread={thread.data} {...(onClose ? { onClose } : {})} popout={popout} />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <MessageList messages={items} myUserId={me?.user.id ?? null} />
      </div>
      {thread.data.archivedAt ? (
        <Alert tone="info">{t(`messaging.archived.${thread.data.archivedReason ?? 'manual'}`)}</Alert>
      ) : null}
      {thread.data.canSend ? <Composer threadId={threadId} /> : null}
    </div>
  );
}
